import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { Button, Heading2, Paragraph } from '@shopmuchmore/design-system';
import { trackAdClick, trackAdImpression } from '../useAds';
import { getAdUrl } from '../ads.types';
import type { AdSlot } from '../ads.types';

interface AdBillboardProps {
  data: AdSlot;
  variant?: 'default' | 'compact';
}

function AdBillboardSkeleton({ variant = 'default' }: { variant?: 'default' | 'compact' }) {
  const height = variant === 'compact' ? 'h-48 md:h-64' : 'h-64 md:h-80 lg:h-96';

  return (
    <section className="w-full max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-8">
      <div className={`${height} rounded-3xl bg-gray-200 animate-pulse border border-gray-100`} />
    </section>
  );
}

function AdBillboard({ data, variant = 'default' }: AdBillboardProps) {
  const navigate = useNavigate();
  const item = data.items[0];

  // Track impression on mount
  useEffect(() => {
    if (item) {
      trackAdImpression(data.slot_id, item.id);
    }
  }, [data.slot_id, item]);

  if (!item) {
    return null;
  }

  const handleClick = () => {
    const url = getAdUrl(item, data.slot_id);
    trackAdClick(data.slot_id, item.id, url);
    console.log(`[AdBillboard] Navigating to: ${url}`);
    navigate(url);
  };

  const handleCtaClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    handleClick();
  };

  const height = variant === 'compact' ? 'h-48 md:h-64' : 'h-64 md:h-80 lg:h-96';

  return (
    <section className="w-full max-w-7xl mx-auto py-6 md:py-8">
      <div
        onClick={handleClick}
        className={`relative ${height} rounded-3xl overflow-hidden cursor-pointer group border-[1.5px] border-gray-100 transition-all duration-300`}
      >
        {/* Background Image */}
        <div
          className="absolute inset-0 bg-cover bg-center transition-transform duration-500 group-hover:scale-105"
          style={{ backgroundImage: `url(${item.desktop_image || item.image || item.mobile_image})` }}
        />

        {/* Gradient Overlay */}
        <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/40 to-transparent" />

        {/* Content */}
        <div className="relative h-full flex items-center">
          <div className="w-full max-w-2xl px-8 md:px-12 lg:px-16">
            <Heading2 className="text-2xl md:text-4xl lg:text-5xl text-white mb-4 ">
              {item.headline}
            </Heading2>
            {item.description && (
              <Paragraph leading="relaxed" className="text-white/90 mb-6 text-sm md:text-base lg:text-lg max-w-lg">
                {item.description}
              </Paragraph>
            )}
            {item.cta_text && (
              <Button
                onClick={handleCtaClick}
                className="gap-2 bg-white text-gray-900 hover:bg-gray-100"
              >
                {item.cta_text}
                <ArrowRight className="size-4" />
              </Button>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

export { AdBillboard, AdBillboardSkeleton };
export default AdBillboard;